import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const { clear } = useCart();
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 8) {
        setStatus("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) {
            clear();
            clearedRef.current = true;
          }
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  return (
    <div
      data-testid="checkout-success-page"
      className="max-w-2xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center"
    >
      {status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto text-[#E25C46] animate-spin" />
          <h1 className="font-serif text-4xl mt-8 text-[#2D2926] font-light">
            Confirming your payment…
          </h1>
          <p className="text-[#5B5653] mt-4">This will only take a moment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#3E6649]" />
          <p className="label-eyebrow mt-8">Payment Received</p>
          <h1 className="font-serif text-5xl mt-4 text-[#2D2926] font-light">Thank you</h1>
          <p className="text-[#5B5653] mt-6 leading-relaxed">
            Your artwork is now yours. The artist has been notified and will arrange free
            delivery to the address you provided. You will receive a confirmation by email.
          </p>
          <Link to="/" className="btn-primary mt-12 inline-flex">
            Return to the Gallery
          </Link>
        </>
      )}
      {(status === "error" || status === "expired" || status === "timeout") && (
        <>
          <AlertCircle className="w-12 h-12 mx-auto text-[#B73225]" />
          <h1 className="font-serif text-4xl mt-8 text-[#2D2926] font-light">
            {status === "expired"
              ? "Checkout session expired"
              : status === "timeout"
              ? "Still confirming…"
              : "Something went wrong"}
          </h1>
          <p className="text-[#5B5653] mt-4">
            {status === "timeout"
              ? "Please check your email — we'll send confirmation as soon as the payment clears."
              : "Please try again or contact the artist."}
          </p>
          <Link to="/" className="btn-secondary mt-10 inline-flex">
            Back to Store
          </Link>
        </>
      )}
    </div>
  );
}
